import * as Sentry from "@sentry/react";
import { useState } from "react";
import type { BookingAttachment } from "../../common/types/Attachment";
import { Flex, FlexInline } from "../Common/Flex";
import Label from "../Common/Label";
import "./BookingAttachmentPreview.css";
import { splitFileExtension } from "../../common/utils/file.util";
import { EventEmitter } from "../../EventEmitter";
import { viewAttachment } from "../../utils/view-message-attachment.util";
import Button from "../Common/Buttons";
import EditableInputField from "../Common/EditableInputField";
import Icon, { PictogramIcon } from "../Common/Icon";
import Tag from "../Common/Tag";
import Tooltip from "../Common/Tooltip";

type Props = {
    attachment: BookingAttachment;

    onChangePrivacy?: (
        attachment: BookingAttachment,
        isPublic: boolean,
    ) => Promise<void>;
    onRename?: (attachment: BookingAttachment) => Promise<void>;
    onRemove?: (attachment: BookingAttachment) => Promise<void>;
};

function BookingAttachmentPreview({
    attachment,
    onChangePrivacy,
    onRename,
    onRemove,
}: Props) {
    const isImage = attachment.mimeType?.startsWith("image/");
    const isPDF = attachment.mimeType === "application/pdf";

    const [isDeleting, setIsDeleting] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [renamedAttachment, setAttachmentRenamed] = useState<
        { filename: string; extension: string } | undefined
    >();
    const showRename = renamedAttachment !== undefined;
    const showAttachmentPrivacySettings = onChangePrivacy;

    const attachmentPrivacy = onChangePrivacy ? (
        <>
            <Tooltip
                label="Only the member can see this"
                className="booking-attachment-preview__visible-on-hover"
                placement="left"
            >
                {attachment.isPublic ? (
                    <Button.Ghost
                        onClick={() => {
                            try {
                                onChangePrivacy(attachment, false);
                            } catch (error) {
                                EventEmitter.emit({
                                    name: "showToastMessage",
                                    params: {
                                        type: "error",
                                        message: "Failed to update attachment privacy.",
                                    },
                                });
                                Sentry.captureException(error, {
                                    extra: {
                                        message:
                                            "LMT failed to update attachment privacy",
                                    },
                                });
                            }
                        }}
                    >
                        <Tag.Medium.Bold.N90>Private</Tag.Medium.Bold.N90>
                    </Button.Ghost>
                ) : (
                    <Tag.Medium.Bold.T90>
                        <FlexInline gap="small">
                            <Icon.Check.XSmall.T50 />
                            Private
                        </FlexInline>
                    </Tag.Medium.Bold.T90>
                )}
            </Tooltip>

            <Tooltip
                label="Member can share this with anyone"
                className="booking-attachment-preview__visible-on-hover"
                placement="left"
            >
                {attachment.isPublic ? (
                    <Tag.Medium.Bold.P90>
                        <FlexInline gap="small">
                            <Icon.Check.XSmall.P50 />
                            Public
                        </FlexInline>
                    </Tag.Medium.Bold.P90>
                ) : (
                    <Button.Ghost
                        onClick={() => {
                            try {
                                onChangePrivacy(attachment, true);
                            } catch (error) {
                                EventEmitter.emit({
                                    name: "showToastMessage",
                                    params: {
                                        type: "error",
                                        message: "Failed to update attachment privacy.",
                                    },
                                });
                                Sentry.captureException(error, {
                                    extra: {
                                        message:
                                            "LMT failed to update attachment privacy",
                                    },
                                });
                            }
                        }}
                    >
                        <Tag.Medium.Bold.N90>Public</Tag.Medium.Bold.N90>
                    </Button.Ghost>
                )}
            </Tooltip>

            <Tooltip
                label={
                    attachment.isPublic
                        ? "Member can share this with anyone"
                        : "Only the member can see this"
                }
                className="booking-attachment-preview__invisible-on-hover"
            >
                {attachment.isPublic ? (
                    <Tag.Medium.Bold.P90>
                        <FlexInline gap="small">
                            <Icon.Check.XSmall.P50 />
                            Public
                        </FlexInline>
                    </Tag.Medium.Bold.P90>
                ) : (
                    <Tag.Medium.Bold.T90>
                        <FlexInline gap="small">
                            <Icon.Check.XSmall.T50 />
                            Private
                        </FlexInline>
                    </Tag.Medium.Bold.T90>
                )}
            </Tooltip>
        </>
    ) : null;

    return (
        <Flex.Row className="booking-attachment-preview" gap="medium">
            <Tooltip label="View">
                <Button.Ghost
                    onClick={() => {
                        void viewAttachment(attachment);
                    }}
                >
                    <div className="booking-attachment-preview__icon">
                        {isImage && (
                            <img src={attachment.url} alt={attachment.filename} />
                        )}
                        {isPDF && <PictogramIcon.Description.Medium.S90 />}
                    </div>
                </Button.Ghost>
            </Tooltip>
            {showRename && onRename ? (
                <>
                    <EditableInputField
                        initialValue={renamedAttachment.filename}
                        onChange={(value) => {
                            setAttachmentRenamed({
                                filename: value,
                                extension: renamedAttachment.extension,
                            });
                        }}
                    />
                    <Tooltip label="Cancel">
                        <Button.N90
                            onClick={() => {
                                setAttachmentRenamed(undefined);
                            }}
                        >
                            <Icon.Close.Small />
                        </Button.N90>
                    </Tooltip>
                    <Tooltip label="Save">
                        <Button.P90
                            disabled={!renamedAttachment}
                            showLoadingSpinner={isRenaming}
                            onClick={async () => {
                                if (!renamedAttachment) return;
                                setIsRenaming(true);
                                try {
                                    await onRename({
                                        ...attachment,
                                        filename:
                                            renamedAttachment.filename +
                                            renamedAttachment.extension,
                                    });
                                } catch (error) {
                                    EventEmitter.emit({
                                        name: "showToastMessage",
                                        params: {
                                            type: "error",
                                            message: "Failed to rename attachment.",
                                        },
                                    });
                                    Sentry.captureException(error, {
                                        extra: {
                                            message: "LMT failed to rename attachment",
                                        },
                                    });
                                }
                                setIsRenaming(false);
                                setAttachmentRenamed(undefined);
                            }}
                        >
                            <Icon.Check.Small />
                        </Button.P90>
                    </Tooltip>
                </>
            ) : (
                <Flex.Row gap="medium" justifyContent="space-between">
                    <Flex.Column gap="xsmall">
                        <Label.Medium className="booking-attachment-preview__filename">
                            {attachment.filename}
                        </Label.Medium>
                        <Label.Medium.N50>
                            {isPDF && "PDF"}
                            {isImage && "Image"}
                        </Label.Medium.N50>
                    </Flex.Column>

                    <div>
                        <Flex.Row gap="medium">
                            {onRename && (
                                <Tooltip
                                    label="Rename"
                                    className="booking-attachment-preview__visible-on-hover"
                                >
                                    <Button.Ghost
                                        onClick={() => {
                                            setAttachmentRenamed(
                                                splitFileExtension(attachment.filename),
                                            );
                                        }}
                                    >
                                        <Icon.Edit.Medium.N10 />
                                    </Button.Ghost>
                                </Tooltip>
                            )}
                            {onRemove && (
                                <Tooltip
                                    label="Delete"
                                    className="booking-attachment-preview__visible-on-hover"
                                >
                                    <Button.Ghost
                                        showLoadingSpinner={isDeleting}
                                        onClick={async () => {
                                            if (
                                                window.confirm(
                                                    `Delete attachment ${attachment.filename}?`,
                                                )
                                            ) {
                                                setIsDeleting(true);
                                                try {
                                                    await onRemove(attachment);
                                                } catch (error) {
                                                    EventEmitter.emit({
                                                        name: "showToastMessage",
                                                        params: {
                                                            type: "error",
                                                            message:
                                                                "Failed to delete attachment.",
                                                        },
                                                    });
                                                    Sentry.captureException(error, {
                                                        extra: {
                                                            message:
                                                                "LMT failed to delete attachment",
                                                        },
                                                    });
                                                }
                                            }
                                        }}
                                    >
                                        <Icon.Delete.Medium.N10 />
                                    </Button.Ghost>
                                </Tooltip>
                            )}

                            {showAttachmentPrivacySettings && attachmentPrivacy}
                        </Flex.Row>
                    </div>
                </Flex.Row>
            )}
        </Flex.Row>
    );
}

export default BookingAttachmentPreview;
